import { FolderInputIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMoveProject } from '../../../entities/project';
import { identityErrorMessage } from '../../../shared/api/identity-error';
import { Button } from '../../../shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../shared/ui/dialog';
import { Label } from '../../../shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../shared/ui/select';
import { toast } from '../../../shared/ui/sonner';

/**
 * Moves a project to another workspace of the same org (Q5). `targets` are the workspaces where the caller
 * also holds `projects.move` and that are not archived; `move_project` checks all of it again.
 */
export function MoveProjectButton({
  project,
  targets,
  onMoved,
}: {
  project: { id: string; name: string };
  targets: readonly { id: string; name: string; slug: string }[];
  onMoved: (target: { id: string; slug: string }) => void;
}) {
  const { t } = useTranslation('project');
  const move = useMoveProject(project.id);
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [error, setError] = useState<string | null>(null);

  function change(next: boolean) {
    if (!next) {
      setTargetId('');
      setError(null);
    }
    setOpen(next);
  }

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <FolderInputIcon aria-hidden="true" />
        {t('move.action')}
      </Button>
      <Dialog open={open} onOpenChange={change}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('move.title', { name: project.name })}</DialogTitle>
            <DialogDescription>{t('move.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="move-project-target">{t('move.target')}</Label>
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger id="move-project-target" className="w-full">
                <SelectValue placeholder={t('move.placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {targets.map((target) => (
                  <SelectItem key={target.id} value={target.id}>
                    {target.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="secondary" onClick={() => change(false)}>
              {t('move.cancel')}
            </Button>
            <Button
              disabled={move.isPending}
              onClick={async () => {
                const target = targets.find((w) => w.id === targetId);
                if (!target) return setError(t('move.required'));
                setError(null);
                try {
                  await move.mutateAsync(target.id);
                  toast.success(t('move.done'));
                  change(false);
                  onMoved(target);
                } catch (e) {
                  setError(identityErrorMessage(e));
                }
              }}
            >
              {t('move.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
